import React, { useContext, useEffect, useState } from 'react';
import * as nodeUrl from 'url';
import IframeResizer from 'iframe-resizer-react';
import { Auth } from '@mdlinx/shared';

import { authIframeUrl } from './auth-reg-modal';
import { AuthRegContext } from './auth-reg-provider';
import { Box } from 'rebass';

const URL = typeof window === 'undefined' ? nodeUrl.URL : window.URL;

const REDIRECT_DESTINATION_AFTER_LOGIN = 'redirectDestinationAfterLogin';

export type InWellPaywallEntryLocation =
  | ''
  | 'REGISTRATION'
  | 'ACQUISITION'
  | 'HIGHLIGHTS_ACQUISITION';

export type SetInWellPaywallState = (args: {
  entryLocation: InWellPaywallEntryLocation;
  enabled: boolean;
}) => any;

export type EnableInWellPaywall = (args: {
  entryLocation: InWellPaywallEntryLocation;
  modalIsDismissable?: boolean;
}) => any;
export type DisableInWellPaywall = () => any;

type Props = {
  children: React.ReactNode;
};

const getInitialHeight = (
  entryLocation: InWellPaywallEntryLocation
): number => {
  switch (entryLocation) {
    case 'REGISTRATION': {
      return 566;
    }
    case 'ACQUISITION': {
      return 333;
    }
    case 'HIGHLIGHTS_ACQUISITION': {
      return 333;
    }
    default: {
      return 608;
    }
  }
};

export const InWellPaywall: React.FC<Props> = ({ children }) => {
  const {
    inWellPaywallState,
    disableInWellPaywall,
    authBaseUrl,
    onLoginSucceeded,
    authState,
  } = useContext(AuthRegContext);
  const inBrowser = (process as any).browser;
  const { entryLocation, enabled } = inWellPaywallState;
  const [displayInWellPaywall, setDisplayInWellPaywall] = useState(false);

  useEffect(() => {
    const nextDisplayInWellPaywall = enabled && authState === 'ANONYMOUS';
    if (nextDisplayInWellPaywall !== displayInWellPaywall) {
      setDisplayInWellPaywall(nextDisplayInWellPaywall);
    }
  }, [enabled, authState]);

  useEffect(() => {
    const handleAuthIframeMessage = (event: MessageEvent) => {
      if (event.origin !== new URL(authBaseUrl).origin) {
        // Message did not originate from auth iframe
        return;
      }

      switch (event.data) {
        case Auth.loginSucceeded: {
          // clearAuthQueryParams();
          onLoginSucceeded();
          disableInWellPaywall();
          return;
        }
        case Auth.highlightsLoginSucceeded: {
          // clearAuthQueryParams();
          onLoginSucceeded(Auth.highlightsLoginSucceeded);
          return;
        }
        case Auth.loginLinkSent: {
          window.localStorage.setItem(
            REDIRECT_DESTINATION_AFTER_LOGIN,
            window.location.href
          );
          return;
        }
        default: {
          return;
        }
      }
    };

    window.addEventListener('message', handleAuthIframeMessage);

    return function cleanup() {
      window.removeEventListener('message', handleAuthIframeMessage);
    };
  }, []);

  const pageSearchParams =
    !!inBrowser && new URL(window.location.href).searchParams;
  const issueType = !!inBrowser
    ? new URL(window.location.href).pathname.split('/')[2]
    : '';

  const obscureContentStyles: any = enabled
    ? {
        height: '300px',
        maxHeight: '300px',
        overflow: 'hidden',
        position: 'relative',
      }
    : {};

  return (
    <>
      <div style={obscureContentStyles}>
        {children}
        {displayInWellPaywall ? (
          <Box
            style={{
              height: '100%',
              width: '100%',
              background:
                'linear-gradient(180deg, rgba(255,255,255,0) 40%, rgba(255,255,255,1) 95%)',
              position: 'absolute',
              top: 0,
              right: 0,
              left: 0,
              bottom: 0,
            }}
          ></Box>
        ) : null}
      </div>
      {displayInWellPaywall ? (
        <IframeResizer
          src={authIframeUrl({
            baseUrl: authBaseUrl,
            entryLocation,
            ...(pageSearchParams ? { searchParams: pageSearchParams } : {}),
            issueType,
            modalIsDismissable: false,
            inBrowser,
            iframeLocation: 'in-well',
          })}
          style={{
            width: '1px',
            minWidth: '100%',
            border: 0,
            height: getInitialHeight(entryLocation),
          }}
          checkOrigin={false}
        />
      ) : null}
    </>
  );
};
